import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import productSummaryService from "../services/internal/productSummaryService";
import { useAuthContext } from "./AuthContext";
import logger from "../logger";
import { apiGet, apiPut } from "../services/apiService";

const ProductSummaryContext = createContext();

const handleError = (err, setError) => {
  logger.error(err);
  if (process.env.NODE_ENV !== "production") {
    setError(err.message);
  }
};

export const ProductSummaryProvider = ({ children }) => {
  const { authInfoUser } = useAuthContext(); // Use authInfoUser from AuthContext
  const userId = authInfoUser?.userId; // Extract userId from authInfoUser
  const [productSummary, setProductSummary] = useState(null);
  const [targetProfile, setTargetProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch target profile from the back-end API
  const loadTargetProfile = useCallback(async () => {
    if (!authInfoUser || !userId) return; // Ensure authInfoUser is available
    try {
      const data = await apiGet(`api/profile/${userId}`);
      setTargetProfile(data);
      logger.info("Target profile loaded data:", data);
    } catch (err) {
      handleError(err, setError);
    }
  }, [authInfoUser, userId]);

  // Fetch product summary on mount or when authInfoUser changes
  useEffect(() => {
    logger.info("ProductSummaryProvider: Fetching product summary for userId:", userId);
    if (!authInfoUser || !userId) return; // Ensure authInfoUser is available

    const loadProductSummary = async () => {
      setLoading(true);
      try {
        const data = await productSummaryService.fetchProductSummary(userId);
        setProductSummary(data);
        await loadTargetProfile(); // Load target profile after fetching product summary
      } catch (err) {
        handleError(err, setError);
      } finally {
        setLoading(false);
      }
    };

    loadProductSummary();
  }, [authInfoUser, userId, loadTargetProfile]);

  // Save product summary via API
  const saveProductSummary = useCallback(
    async (summaryData) => {
      if (!authInfoUser || !userId) return; // Ensure authInfoUser is available
      try {
        const updatedSummary = await productSummaryService.handleSaveSummary(userId, summaryData);
        setProductSummary(updatedSummary);
        loadTargetProfile(); // HACK TO GET COMPLETION PERCENTAGE TO UPDATE
      } catch (err) {
        handleError(err, setError);
      }
    },
    [authInfoUser, userId, loadTargetProfile]
  );

  // Log targetProfile whenever it changes
  useEffect(() => {
    if (targetProfile) {
      logger.info("Target profile loaded targetProfile:", targetProfile);
    }
  }, [targetProfile]);

  const saveTargetProfileSection = useCallback(
    async (section, data) => {
      if (!authInfoUser || !userId) return; // Ensure authInfoUser is available
      try {
        const profile = await apiPut(`api/profile/${userId}/${section}`, data);
        logger.info("Target profile post-save loaded data:", profile);
        setTargetProfile(profile);
      } catch (err) {
        handleError(err, setError);
      }
    },
    [authInfoUser, userId]
  );

  return (
    <ProductSummaryContext.Provider
      value={{
        productSummary,
        saveProductSummary,
        targetProfile,
        loadTargetProfile,
        saveTargetProfileSection,
        loading,
        error,
      }}
    >
      {children}
    </ProductSummaryContext.Provider>
  );
};

export const useProductSummary = () => useContext(ProductSummaryContext);
